import React, { useCallback, useMemo, useRef } from 'react';
import usePaginatedItems from '../../../hooks/usePaginatedItems';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import { salesSearchByOptions, StoreSaleSearchBy } from 'types';
import { useParams } from 'react-router-dom';
import Table from 'router/subrouters/Dashboard/components/Table';
import { useTranslation } from 'react-i18next';
import useLabels from './hooks/useLabels';
import { StoreSale } from 'models/Sale';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import Modal, { ModalRef } from 'components/Modal';
import UploadFileForm from '../../components/UploadFileForm';
import { toast } from 'react-toastify';
import utils from 'utils';

const StoreSales = () => {
  const { t } = useTranslation();
  const labels = useLabels();

  const { storeNo } = useParams();

  const modalRef = useRef<ModalRef>();

  const {
    setQueryParam,
    params: { page, search, limit, searchBy },
  } = useQueryParams<{
    page: string;
    search: string;
    limit: string;
    searchBy: StoreSaleSearchBy;
  }>({ searchBy: 'name' });

  const { items, isFetching, totalPages, totalItems, isRefreshing, onRefresh } =
    usePaginatedItems({
      queryKey: 'store-sales',
      makeRequest: api.sales.getStoreSales,
      searchParam: searchBy,
      params: {
        limit,
        page,
        [searchBy]: !!search && search.length >= 2 ? search : undefined,
        storeNo,
      },
    });

  const searchByOptions = useMemo(
    () =>
      Object.values(salesSearchByOptions).map(({ value, label }) => ({
        label: t(label),
        value,
      })),
    [t],
  );

  const handleSearchByChange = useCallback(
    (option: OptionValue<any>) => {
      setQueryParam('searchBy', option.value, true);
    },
    [setQueryParam],
  );

  const calculatePrice = useCallback((quantity: number, price: number) => {
    return (quantity * price).toFixed(2);
  }, []);
  const renderContentArray = useCallback(
    (data: StoreSale[]) =>
      data?.map(
        ({
          quantity,
          price,
          productCatalog: { name, imageUrlEn },
          ...rest
        }) => ({
          imageUrl: imageUrlEn,
          quantity,
          price,
          totalPrice: calculatePrice(quantity, price),
          ...rest,
        }),
      ),
    [calculatePrice],
  );

  const onUploadFile = useCallback(
    async ({ attachment }: { attachment: File[] }) => {
      try {
        const formData = new FormData();
        formData.append('file', attachment[0]);
        await api.sales.uploadSales(formData, storeNo);
        toast.success(t('successUploadingSalesFile'));
        modalRef.current.close();
        onRefresh();
      } catch (error) {
        toast.error(utils.handleResponseError(error));
      }
    },
    [onRefresh, storeNo, t],
  );

  return (
    <>
      <Table
        title={t('Sales')}
        labels={labels}
        contentArray={renderContentArray(items)}
        totalItems={totalItems}
        fetching={isFetching}
        totalPages={totalPages}
        searchBy={searchBy}
        searchable
        onUploadProducts={() => {
          modalRef.current.open();
        }}
        searchPlaceholder={t('SaleSearch')}
        searchByOptions={searchByOptions}
        onSearchByChange={handleSearchByChange}
        elementProps={labels.map(({ key }) => key)}
        uploadButtonText="uploadSales"
      />
      <Modal
        focusableElements="div"
        type="no-action"
        ref={modalRef}
        modalName="upload-sales"
        title={t('uploadSales')}
      >
        <UploadFileForm
          onSubmit={onUploadFile}
          inProgress={isRefreshing}
          handleCancel={() => {
            modalRef.current.close();
          }}
          formType="salesUpload"
          fileType=".xlsx"
        />
      </Modal>
    </>
  );
};

export default StoreSales;
